/**
 * 검색 가능한 드롭다운 컴포넌트
 *
 * 네이티브 select 대신 커스텀 드롭다운으로 검색/필터링을 지원합니다.
 * 키보드 탐색 (↑↓ Enter Escape), 외부 클릭 닫힘, 접근성을 지원합니다.
 */

'use client';

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AppComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface AppComboboxProps {
  options: readonly AppComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
}

export function AppCombobox({
  options,
  value,
  onChange,
  placeholder = '선택하세요',
  searchPlaceholder = '검색...',
  label,
  disabled = false,
  className,
  emptyMessage = '결과 없음',
}: AppComboboxProps) {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // 필터링된 옵션
  const filtered = useMemo(() => {
    if (!search.trim()) {return options;}
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  // 선택된 라벨
  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || '',
    [options, value]
  );

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!isOpen) {return;}
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // 열릴 때 검색 포커스
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setHighlightIndex(-1);
    }
  }, [isOpen]);

  // 하이라이트된 항목 스크롤
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightIndex >= 0 && filtered[highlightIndex] && !filtered[highlightIndex].disabled) {
          handleSelect(filtered[highlightIndex].value);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    },
    [filtered, highlightIndex, handleSelect]
  );

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
        </label>
      )}

      {/* 트리거 버튼 */}
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {setIsOpen(!isOpen);}
        }}
        className={cn(
          'input-base flex items-center justify-between gap-2 text-left cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          isOpen && 'ring-2 ring-primary-500 border-transparent'
        )}
      >
        <span className={cn('truncate', !value && 'text-neutral-400')}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-neutral-400 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* 드롭다운 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden">
          {/* 검색 입력 */}
          <div className="p-2 border-b border-neutral-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setHighlightIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-8 py-2 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    inputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* 옵션 목록 */}
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            className="max-h-48 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-sm text-neutral-400 text-center">
                {emptyMessage}
              </li>
            ) : (
              filtered.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled}
                  onClick={() => {
                    if (!option.disabled) {handleSelect(option.value);}
                  }}
                  className={cn(
                    'px-3 py-2.5 text-sm cursor-pointer transition-colors',
                    option.value === value && 'bg-purple-50 text-purple-700 font-medium',
                    index === highlightIndex && 'bg-neutral-100',
                    option.disabled
                      ? 'text-neutral-300 cursor-not-allowed'
                      : 'hover:bg-neutral-50'
                  )}
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
